// src/app/services/language-detector.service.ts
import { Injectable } from '@angular/core';
import { ActivityLogService } from './activity-log';
import { countryToAlpha2 } from 'country-to-iso';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

interface IsoLanguageCodes {
  [countryCode: string]: string;
}

/**
 * Service responsible for detecting languages based on location information.
 * Provides functionality to:
 * - Map countries to their primary languages
 * - Detect languages for cities and regions
 * - Support ISO language code standards
 * 
 * @description
 * This service maintains mappings between locations and their associated languages,
 * supporting both country-level and city/region-level language detection.
 */
@Injectable({
  providedIn: 'root',
})
export class LanguageDetectorService {
  // Map of countries to their primary languages
  private countryLanguageMap = new Map<string, string[]>();
  private cityRegionLanguageMap = new Map<string, string[]>();
  private isoLanguageCodes: IsoLanguageCodes = {};

  constructor(
    private activityLogService: ActivityLogService,
    private http: HttpClient
  ) {
    this.activityLogService.logEvent(
      'LanguageDetectorServiceInitialized',
      'service_lifecycle'
    );
    this.initializeMaps();
  }

  private async initializeMaps(): Promise<void> {
    try {
      this.initializeHardcodedMappings();
      
      this.isoLanguageCodes = await firstValueFrom(
        this.http.get<IsoLanguageCodes>('/assets/data/iso-language-codes.json')
      );
      
      Object.entries(this.isoLanguageCodes).forEach(([countryCode, languages]) => {
        if (languages && !this.countryLanguageMap.has(countryCode)) {
          const languageArray = languages.split(',').map((lang: string) => lang.trim());
          this.countryLanguageMap.set(countryCode, languageArray);
        }
      });
      
      this.initializeCityRegionLanguageMap();
    } catch (error) {
      this.activityLogService.logEvent(
        'LanguageCodesLoadFailed',
        'service_lifecycle',
        {
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      );
    }
  }

  private initializeHardcodedMappings(): void {
    const hardcodedMappings = {
      'US': ['en'],
      'GB': ['en'],
      'CA': ['en', 'fr'],
      'MX': ['es'],
      'JP': ['ja'],
      'KR': ['ko'],
      'DE': ['de'],
      'FR': ['fr'],
      'IT': ['it'],
      'ES': ['es'],
      'RU': ['ru'],
      'BR': ['pt-BR'],
      'IN': ['hi', 'en'],
      'AU': ['en'],
      'NZ': ['en'],
      'CN': ['zh-CN', 'yue', 'wuu', 'dta', 'ug', 'za']
    };

    Object.entries(hardcodedMappings).forEach(([countryCode, languages]) => {
      this.countryLanguageMap.set(countryCode, languages);
    });
  }

  private initializeCityRegionLanguageMap(): void {
    this.cityRegionLanguageMap.set('Shanghai', ['zh-CN', 'wuu']);
    this.cityRegionLanguageMap.set('Hong Kong', ['zh-CN', 'yue']);
  }

  private getCountryCode(country: string): string | undefined {
    if (!country) return undefined;
    
    if (country.length === 2 && country.toUpperCase() === country) {
      return country;
    }

    try {
      return countryToAlpha2(country) || undefined;
    } catch (error) {
      this.activityLogService.logEvent(
        'CountryCodeConversionFailed',
        'language_detection',
        {
          country,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      );
      return undefined;
    }
  }

  /**
   * Detects all languages for a location based on its name and country.
   * @param locationName - The name of the location (city, town, etc.)
   * @param country - The country of the location
   * @returns Array of detected languages or empty array if none determinable
   */
  detectLanguages(locationName: string, country?: string): string[] {
    const detectedLanguages = new Set<string>();

    const cityLanguages = this.cityRegionLanguageMap.get(locationName);
    if (cityLanguages) {
      cityLanguages.forEach(lang => detectedLanguages.add(lang));
      this.activityLogService.logEvent(
        'LanguagesDetectedByCity',
        'language_detection',
        {
          locationName,
          detectedLanguages: Array.from(detectedLanguages)
        }
      );
    }

    if (country) {
      const countryCode = this.getCountryCode(country);
      if (countryCode) {
        const countryLanguages = this.countryLanguageMap.get(countryCode);
        if (countryLanguages) {
          countryLanguages.forEach(lang => detectedLanguages.add(lang));
          this.activityLogService.logEvent(
            'LanguagesDetectedByCountry',
            'language_detection',
            {
              country,
              countryCode,
              detectedLanguages: Array.from(detectedLanguages)
            }
          );
        }
      }
    }

    if (!country) {
      for (const [city, languages] of this.cityRegionLanguageMap.entries()) {
        if (locationName.includes(city)) {
          languages.forEach(lang => detectedLanguages.add(lang));
          this.activityLogService.logEvent(
            'LanguagesDetectedByPartialMatch',
            'language_detection',
            {
              locationName,
              matchedCity: city,
              detectedLanguages: Array.from(detectedLanguages)
            }
          );
        }
      }
    }

    const result = Array.from(detectedLanguages);
    if (result.length === 0) {
      this.activityLogService.logEvent(
        'LanguageDetectionFailed',
        'language_detection',
        {
          locationName,
          country
        }
      );
    }

    return result;
  }

  /**
   * @deprecated Use detectLanguages() instead
   * Detects the primary language for a location.
   * @param locationName - The name of the location
   * @param country - The country of the location
   * @returns The primary detected language or undefined if none determinable
   */
  detectLanguage(locationName: string, country?: string): string | undefined {
    const languages = this.detectLanguages(locationName, country);
    return languages.length > 0 ? languages[0] : undefined;
  }

  /**
   * Gets a list of all supported languages in the system.
   * @returns An array of all supported language names
   */
  getSupportedLanguages(): string[] {
    const languagesSet = new Set<string>();
    
    this.countryLanguageMap.forEach(langs => {
      langs.forEach(lang => {
        languagesSet.add(lang);
      });
    });
    
    this.cityRegionLanguageMap.forEach(langs => {
      langs.forEach(lang => {
        languagesSet.add(lang);
      });
    });
    
    return Array.from(languagesSet).sort();
  }

  /**
   * Gets a list of all supported countries.
   * @returns An array of all countries in the database
   */
  getSupportedCountries(): string[] {
    return Array.from(this.countryLanguageMap.keys()).sort();
  }
}